/**
 * components/object/ObjectDocuments.jsx — Iter 52 Documents tab.
 *
 * Drawing-first: documents BELONG to the focused object. The tab
 * renders:
 *   - a tight inline upload row (file picker + type dropdown)
 *   - a list of documents (thumbnail + title + type chip)
 *   - tap a document → expand inline (image) or open native browser
 *     PDF in the embedded viewer drawer
 *
 * NO folder browse. NO library page. Documents are intelligence, not
 * storage.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Upload, Loader2, FileText, Trash2, Eye, ExternalLink, X,
} from "lucide-react";
import {
  listObjectDocuments, uploadObjectDocument, deleteDocument,
  documentThumbnailUrl, DOCUMENT_TYPES, DOCUMENT_TYPE_LABEL,
} from "@/lib/documents";
import EmbeddedDocumentViewer from "./EmbeddedDocumentViewer";

export default function ObjectDocuments({ objectType, objectId, onChanged }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState("SHOP_DRAWING");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    const rows = await listObjectDocuments(objectType, objectId);
    setDocs(rows);
    setLoading(false);
  };

  useEffect(() => {
    if (!objectType || !objectId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, objectId]);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setError(null); setProgress(0);
    try {
      await uploadObjectDocument(objectType, objectId, {
        file, documentType: docType,
        onProgress: (p) => setProgress(p),
      });
      await reload();
      onChanged && onChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setBusy(false); setProgress(0);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await deleteDocument(doc.id);
      await reload();
      onChanged && onChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Delete failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="object-documents"
          data-count={docs.length}>
      {/* Inline uploader — no separate modal */}
      <div
        className="fop-surface p-3 rounded-md"
        data-testid="object-documents-upload"
      >
        <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)] mb-2 flex items-center gap-1.5">
          <Upload size={12} /> Upload Document
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            disabled={busy}
            className="text-[11px] px-2 py-1.5 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text)] focus:outline-none"
            data-testid="object-documents-type"
          >
            {DOCUMENT_TYPES.map((t) => (
              <option key={t} value={t}>{DOCUMENT_TYPE_LABEL[t]}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={pick}
            disabled={busy}
            className="text-[11px] px-3 py-1.5 rounded border border-[var(--fop-primary)] text-[var(--fop-primary)] hover:bg-[var(--fop-primary-soft)] disabled:opacity-60"
            data-testid="object-documents-upload-btn"
          >
            {busy ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" />
                Uploading… {progress}%
              </span>
            ) : "Choose File"}
          </button>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={onFile}
            data-testid="object-documents-file-input"
          />
        </div>
        {error && (
          <div className="text-[11px] text-[var(--st-escalated,#ef4444)] mt-2"
                data-testid="object-documents-error">
            {error}
          </div>
        )}
      </div>

      {/* Document list */}
      {loading ? (
        <div className="px-3 py-6 text-[12px] text-[var(--fop-text-3)] flex items-center gap-1.5"
              data-testid="object-documents-loading">
          <Loader2 size={12} className="animate-spin" /> Loading documents…
        </div>
      ) : docs.length === 0 ? (
        <div className="px-3 py-8 text-center text-[12px] text-[var(--fop-text-3)]"
              data-testid="object-documents-empty">
          No documents attached to this object yet.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="object-documents-list">
          {docs.map((d) => (
            <DocumentRow key={d.id} doc={d}
                          onView={() => setViewing(d)}
                          onDelete={() => remove(d)} />
          ))}
        </ul>
      )}

      {viewing && (
        <EmbeddedDocumentViewer
          doc={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function DocumentRow({ doc, onView, onDelete }) {
  const hasThumb = !!doc.thumb_path;
  const isImage = (doc.content_type || "").startsWith("image/");
  const isPdf = (doc.content_type || "") === "application/pdf"
    || (doc.file_name || "").toLowerCase().endsWith(".pdf");
  return (
    <li
      className="rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface-2)] px-3 py-2 flex gap-3 items-start hover:border-[var(--fop-text-3)] transition-colors cursor-pointer"
      onClick={onView}
      data-testid={`object-document-row-${doc.id}`}
      data-document-id={doc.id}
      data-document-type={doc.document_type}
    >
      {/* Thumb */}
      <div className="w-12 h-16 shrink-0 rounded overflow-hidden bg-[var(--fop-bg)] border border-[var(--fop-border)] flex items-center justify-center">
        {hasThumb ? (
          <Thumb docId={doc.id} alt={doc.title} />
        ) : (
          <FileText size={18} className="text-[var(--fop-text-3)]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] text-[var(--fop-text)] font-semibold truncate">
          {doc.title}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          <span className="px-1.5 py-0.5 rounded border border-[var(--fop-border)] text-[var(--fop-text-2)]">
            {DOCUMENT_TYPE_LABEL[doc.document_type] || doc.document_type}
          </span>
          <span>{prettySize(doc.file_size)}</span>
          {doc.sheet_revision && <span>REV {doc.sheet_revision}</span>}
          {doc.station_label && (
            <span className="text-[var(--fop-primary)]">{doc.station_label}</span>
          )}
        </div>
        <div className="text-[10px] text-[var(--fop-text-3)] truncate mt-0.5">
          {doc.file_name}
        </div>
      </div>
      <div className="flex flex-col gap-1 items-end shrink-0">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onView(); }}
          className="text-[var(--fop-text-3)] hover:text-[var(--fop-primary)]"
          title={isImage || isPdf ? "Preview" : "Open"}
          data-testid={`object-document-view-${doc.id}`}
          aria-label="View document"
        >
          {isImage || isPdf ? <Eye size={13} /> : <ExternalLink size={13} />}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-[var(--fop-text-3)] hover:text-[var(--st-escalated,#ef4444)]"
          title="Delete"
          data-testid={`object-document-delete-${doc.id}`}
          aria-label="Delete document"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </li>
  );
}

function Thumb({ docId, alt }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancel = false;
    let blobUrl = null;
    import("@/lib/documents").then(async ({ fetchBlobUrl }) => {
      try {
        blobUrl = await fetchBlobUrl(documentThumbnailUrl(docId));
        if (!cancel) setSrc(blobUrl);
      } catch {/* swallow */ }
    });
    return () => {
      cancel = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [docId]);
  if (!src) {
    return <div className="w-full h-full bg-[var(--fop-bg)]" />;
  }
  return <img src={src} alt={alt}
                className="w-full h-full object-cover"
                draggable={false} />;
}

function prettySize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u += 1; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[u]}`;
}
